import { useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Shield, Flame, Download, Trash2, FileText, Eye, Share2, Send, Upload, Copy, Mail, CheckCircle2, Ban, RotateCcw, Archive, ArchiveRestore, BadgeCheck } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCertificadosInstalacion,
  type CertificadoInstalacion,
} from '@/hooks/useCertificadosInstalacion';
import { CertificadoFormDialog } from './CertificadoFormDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { generarCertificadoPdf } from '@/lib/certificados/certificadoPdf';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { supabase } from '@/integrations/supabase/client';

const ESTADO_VARIANT: Record<string, { label: string; variant: any }> = {
  borrador: { label: 'Borrador', variant: 'secondary' },
  pendiente_firma: { label: 'Pendiente de firma', variant: 'outline' },
  emitido: { label: 'Emitido', variant: 'default' },
  anulado: { label: 'Anulado', variant: 'destructive' },
};

function CertEstadoTimeline({ c }: { c: CertificadoInstalacion }) {
  const generadoAt = c.created_at;
  const enviadoAt = c.firma_enviado_at;
  const enviado = !!enviadoAt || !!c.token_firma;
  const firmadoAt = c.fecha_firma;
  const firmado = !!firmadoAt;
  const anuladoAt = c.fecha_anulacion;
  const anulado = c.estado === 'anulado' || !!anuladoAt;
  const pasos: Array<{ label: string; done: boolean; date?: string | null; icon: any; tone?: 'danger' }> = [
    { label: 'Generado', done: true, date: generadoAt, icon: FileText },
    { label: 'Enviado a firma', done: enviado, date: enviadoAt, icon: Send },
    { label: firmado ? `Firmado${c.firma_origen ? ` (${c.firma_origen})` : ''}` : 'Firmado', done: firmado, date: firmadoAt, icon: CheckCircle2 },
  ];
  if (anulado) pasos.push({ label: 'Anulado', done: true, date: anuladoAt, icon: Ban, tone: 'danger' });
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-2">
      {pasos.map((p, i) => {
        const Icon = p.icon;
        const toneClass = p.done
          ? (p.tone === 'danger'
              ? 'bg-destructive/10 border-destructive/30 text-destructive'
              : 'bg-primary/10 border-primary/30 text-primary')
          : 'bg-muted text-muted-foreground border-transparent';
        return (
          <div key={i} className="flex items-center gap-1.5">
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${toneClass}`}>
              <Icon className="h-3 w-3" />
              <span>{p.label}</span>
              {p.done && p.date && (
                <span className="opacity-70">· {format(new Date(p.date), 'd MMM yyyy', { locale: es })}</span>
              )}
            </div>
            {i < pasos.length - 1 && <div className="w-3 h-px bg-border" />}
          </div>
        );
      })}
    </div>
  );
}

interface Props {
  clienteId: string;
}

export function CertificadosClienteTab({ clienteId }: Props) {
  const { perfil, empresa } = useAuth();
  const { clientes, sistemas, sedes, elementos } = useSecurityContext();
  const {
    certificados, loading, reload, descargarPdf, verPdf, compartirPdf,
    eliminarCertificado, actualizarCertificado, subirPdf,
    generarLinkFirma, subirCertificadoFirmado,
  } = useCertificadosInstalacion(clienteId);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [aBorrar, setABorrar] = useState<CertificadoInstalacion | null>(null);
  const [regenerandoId, setRegenerandoId] = useState<string | null>(null);
  const [firmaDlg, setFirmaDlg] = useState<{ cert: CertificadoInstalacion; email: string; url: string | null } | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviandoCorreo, setEnviandoCorreo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subirFirmadoDlg, setSubirFirmadoDlg] = useState<CertificadoInstalacion | null>(null);
  const [subiendoFirmado, setSubiendoFirmado] = useState(false);
  const [anularDlg, setAnularDlg] = useState<CertificadoInstalacion | null>(null);
  const [mostrarArchivados, setMostrarArchivados] = useState(false);

  const cliente = clientes.find(c => c.id === clienteId);
  const visibles = certificados.filter(c => mostrarArchivados || !c.archivado);
  const archivadosCount = certificados.filter(c => c.archivado).length;

  const regenerar = async (c: CertificadoInstalacion, opts?: { forzarDefinitivo?: boolean }) => {
    if (!cliente) {
      toast.error('Cliente no disponible');
      return;
    }
    setRegenerandoId(c.id);
    try {
      const sistemasInfo = c.sistemas_ids.map(id => {
        const s = sistemas.find(x => x.id === id);
        const sede = s?.sede_id ? sedes.find(se => se.id === s.sede_id) : null;
        const direccionPartes = sede
          ? [sede.direccion, sede.codigo_postal, sede.poblacion, sede.provincia].filter(Boolean).join(', ')
          : '';
        const elementosSistema = elementos
          .filter(e => e.sistema_id === id)
          .sort((a: any, b: any) => (a.numero_elemento ?? 0) - (b.numero_elemento ?? 0));
        return {
          tipo: s?.tipo || '',
          tipoLabel: s ? getSystemTypeLabel(s.tipo) : '',
          sedeNombre: sede?.nombre || undefined,
          sedeDireccion: direccionPartes || null,
          elementos: elementosSistema.map((e: any) => ({
            nombre: e.nombre,
            tipo_equipo: e.tipo_equipo,
            marca: e.marca,
            modelo: e.modelo,
            mac_serie: e.mac_serie,
            ubicacion: e.area_particion || null,
            zona: e.zona,
            anio_fabricacion: e.anio_fabricacion,
          })),
        };
      });


      const sedeElegida = c.sede_id ? sedes.find(s => s.id === c.sede_id) : null;
      const sedeDirParts = sedeElegida
        ? [sedeElegida.direccion, sedeElegida.codigo_postal, sedeElegida.poblacion, sedeElegida.provincia]
            .filter(Boolean).join(', ')
        : '';

      const blob = await generarCertificadoPdf({
        empresaId: cliente.empresa_id!,
        cliente,
        certificado: {
          numero_formateado: c.numero_formateado,
          tipo: c.tipo,
          fecha_instalacion: c.fecha_instalacion,
          descripcion_instalacion: c.descripcion_instalacion,
          referencia_proyecto: c.referencia_proyecto,
          grado_intrusion: c.grado_intrusion,
          sedeNombre: sedeElegida?.nombre || null,
          sedeDireccion: sedeDirParts || null,
          objeto: c.objeto,
          clausulas: c.clausulas,
          texto_legal: c.texto_legal,
          observaciones: c.observaciones,
          firma_cliente: c.firma_cliente,
          firma_empresa: c.firma_empresa,
          nombre_firmante_cliente: c.nombre_firmante_cliente,
        },
        sistemas: sistemasInfo,
        esBorrador: opts?.forzarDefinitivo ? false : c.estado === 'borrador',
      });

      await subirPdf(c, blob);
      if (!opts?.forzarDefinitivo) toast.success('PDF generado');
    } catch (e: any) {
      toast.error(e.message || 'Error al generar PDF');
      throw e;
    } finally {
      setRegenerandoId(null);
    }
  };

  const esOffice = perfil?.rol === 'admin' || perfil?.rol === 'gestor';

  if (!esOffice) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          No tienes permisos para ver los certificados de este cliente.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-semibold text-lg">Certificados de instalación</h3>
          <p className="text-sm text-muted-foreground">
            Acreditación de instalaciones de seguridad y PCI entregadas al cliente
          </p>
        </div>
        <div className="flex items-center gap-3">
          {archivadosCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Switch
                id="ver-cert-archivados"
                checked={mostrarArchivados}
                onCheckedChange={setMostrarArchivados}
              />
              <Label htmlFor="ver-cert-archivados" className="cursor-pointer">
                Ver archivados ({archivadosCount})
              </Label>
            </div>
          )}
          <Button onClick={() => setDlgOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Nuevo certificado
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-center py-8">Cargando…</p>
      ) : visibles.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <BadgeCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
            {certificados.length === 0
              ? 'No hay certificados para este cliente todavía.'
              : 'No hay certificados visibles. Activa "Ver archivados" para mostrarlos.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {visibles.map(c => {
            const est = ESTADO_VARIANT[c.estado] || ESTADO_VARIANT.borrador;
            const TipoIcon = c.tipo === 'seguridad' ? Shield : Flame;
            const esAnulado = c.estado === 'anulado';
            return (
              <Card key={c.id} className={`hover:shadow-md transition-shadow ${c.archivado ? 'opacity-70' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`rounded-md p-2 ${c.tipo === 'seguridad' ? 'bg-blue-500/10 text-blue-600' : 'bg-orange-500/10 text-orange-600'}`}>
                        <TipoIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-semibold">{c.numero_formateado}</span>
                          <Badge variant="outline" className="text-xs">
                            {c.tipo === 'seguridad' ? 'Seguridad' : 'PCI'}
                          </Badge>
                          {c.grado_intrusion && (
                            <Badge variant="secondary" className="text-xs">Grado {c.grado_intrusion}</Badge>
                          )}
                          {(c.estado === 'anulado') && <Badge variant={est.variant}>{est.label}</Badge>}
                          {c.archivado && (
                            <Badge variant="secondary" className="text-xs">Archivado</Badge>
                          )}
                        </div>
                        <CertEstadoTimeline c={c} />
                        <div className="text-sm text-muted-foreground mt-2">
                          Instalado: {format(new Date(c.fecha_instalacion), 'd MMM yyyy', { locale: es })}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {c.sistemas_ids.length} sistema(s)
                          {c.referencia_proyecto && ` · Proyecto ${c.referencia_proyecto}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => verPdf(c)}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> Ver
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => descargarPdf(c)}>
                        <Download className="h-3.5 w-3.5 mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => compartirPdf(c)}>
                        <Share2 className="h-3.5 w-3.5 mr-1" /> Compartir
                      </Button>
                      {c.estado === 'borrador' && !esAnulado && (
                        <Button
                          size="sm"
                          variant="default"
                          disabled={regenerandoId === c.id}
                          onClick={async () => {
                            setRegenerandoId(c.id);
                            try {
                              await regenerar(c, { forzarDefinitivo: true });
                              await actualizarCertificado(c.id, { estado: 'pendiente_firma' } as any);
                              toast.success('Certificado emitido. Ya puedes enviarlo a firmar.');
                            } catch (e: any) {
                              toast.error(e?.message || 'No se pudo emitir');
                            } finally {
                              setRegenerandoId(null);
                            }
                          }}
                          title="Quita la marca de borrador y habilita el envío a firma"
                        >
                          <BadgeCheck className="h-3.5 w-3.5 mr-1" />
                          {regenerandoId === c.id ? 'Emitiendo…' : 'Emitir'}
                        </Button>
                      )}
                      {c.estado !== 'borrador' && !esAnulado && c.estado !== 'emitido' && !c.fecha_firma && !c.firma_cliente && (
                        <>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => setFirmaDlg({ cert: c, email: c.firma_email_destinatario || cliente?.email || '', url: null })}
                            title="Enviar enlace de firma online al cliente"
                          >
                            <Send className="h-3.5 w-3.5 mr-1" /> Enviar a firmar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSubirFirmadoDlg(c)}
                            title="Subir el PDF firmado escaneado"
                          >
                            <Upload className="h-3.5 w-3.5 mr-1" /> Subir firmado
                          </Button>
                        </>
                      )}
                      {c.estado === 'emitido' && !c.fecha_firma && !c.firma_cliente && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setFirmaDlg({ cert: c, email: c.firma_email_destinatario || cliente?.email || '', url: null })}
                          title="Generar nuevo enlace de firma"
                        >
                          <Send className="h-3.5 w-3.5 mr-1" /> Re-enviar firma
                        </Button>
                      )}

                      {!esAnulado && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setAnularDlg(c)}
                          title="Marcar el certificado como anulado"
                        >
                          <Ban className="h-3.5 w-3.5 mr-1" /> Anular
                        </Button>
                      )}
                      {esAnulado && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                await actualizarCertificado(c.id, {
                                  estado: c.fecha_firma ? 'emitido' : 'borrador',
                                  fecha_anulacion: null,
                                } as any);
                                toast.success('Anulación deshecha');
                              } catch (e: any) {
                                toast.error(e.message);
                              }
                            }}
                          >
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reactivar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              try {
                                await actualizarCertificado(c.id, { archivado: !c.archivado } as any);
                                toast.success(c.archivado ? 'Certificado desarchivado' : 'Certificado archivado');
                              } catch (e: any) {
                                toast.error(e.message);
                              }
                            }}
                          >
                            {c.archivado
                              ? <><ArchiveRestore className="h-3.5 w-3.5 mr-1" /> Desarchivar</>
                              : <><Archive className="h-3.5 w-3.5 mr-1" /> Archivar</>}
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setABorrar(c)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CertificadoFormDialog
        open={dlgOpen}
        onOpenChange={setDlgOpen}
        clienteId={clienteId}
        onCreated={reload}
      />

      <AlertDialog open={!!aBorrar} onOpenChange={(o) => !o && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar certificado</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el certificado {aBorrar?.numero_formateado} y su PDF. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!aBorrar) return;
                try {
                  await eliminarCertificado(aBorrar);
                  toast.success('Certificado eliminado');
                  setABorrar(null);
                } catch (e: any) {
                  toast.error(e.message);
                }
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo: Enviar enlace de firma */}
      <Dialog open={!!firmaDlg} onOpenChange={(o) => !o && setFirmaDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar certificado a firmar</DialogTitle>
            <DialogDescription>
              Genera un enlace único para que el cliente firme el certificado {firmaDlg?.cert.numero_formateado} desde su móvil u ordenador.
            </DialogDescription>
          </DialogHeader>
          {firmaDlg && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firma-email-cert">Email del cliente (opcional)</Label>
                <Input
                  id="firma-email-cert"
                  type="email"
                  value={firmaDlg.email}
                  onChange={(e) => setFirmaDlg({ ...firmaDlg, email: e.target.value })}
                  placeholder="cliente@empresa.com"
                />
              </div>
              {firmaDlg.url && (
                <div className="space-y-2">
                  <Label>Enlace de firma</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={firmaDlg.url} className="font-mono text-xs" />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={async () => {
                        await navigator.clipboard.writeText(firmaDlg.url!);
                        toast.success('Enlace copiado al portapapeles');
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Válido durante 30 días.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setFirmaDlg(null)}>Cerrar</Button>
            {firmaDlg?.url ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!firmaDlg) return;
                    const subject = encodeURIComponent(`Firma del certificado ${firmaDlg.cert.numero_formateado}`);
                    const body = encodeURIComponent(
                      `Hola,\n\nAdjunto el enlace para firmar el certificado de instalación ${firmaDlg.cert.numero_formateado}.\n\nFirmar aquí: ${firmaDlg.url}\n\nGracias.`,
                    );
                    window.location.href = `mailto:${firmaDlg.email || ''}?subject=${subject}&body=${body}`;
                  }}
                >
                  <Mail className="h-4 w-4 mr-2" /> Abrir email
                </Button>
                <Button
                  variant="outline"
                  disabled={enviandoCorreo || !firmaDlg.email}
                  onClick={async () => {
                    if (!firmaDlg?.url || !firmaDlg.email) {
                      toast.error('Indica un email del cliente');
                      return;
                    }
                    setEnviandoCorreo(true);
                    try {
                      const portalUrl = `${window.location.origin}/portal/login`;
                      const { error } = await supabase.functions.invoke('send-email', {
                        body: {
                          to: firmaDlg.email,
                          subject: `Firma pendiente: certificado ${firmaDlg.cert.numero_formateado}`,
                          template: 'firma-documento',
                          empresa_id: perfil?.empresa_id,
                          data: {
                            nombre: cliente?.nombre || '',
                            empresa_nombre: empresa?.nombre || '',
                            tipo: 'certificado',
                            numero: firmaDlg.cert.numero_formateado,
                            url: firmaDlg.url,
                            portal_url: portalUrl,
                          },
                        },
                      });
                      if (error) throw error;
                      toast.success('Correo enviado al cliente');
                    } catch (e: any) {
                      toast.error(e?.message || 'No se pudo enviar el correo');
                    } finally {
                      setEnviandoCorreo(false);
                    }
                  }}
                >
                  <Send className="h-4 w-4 mr-2" /> {enviandoCorreo ? 'Enviando…' : 'Enviar por correo'}
                </Button>
                <Button
                  onClick={async () => {
                    await navigator.clipboard.writeText(firmaDlg.url!);
                    toast.success('Enlace copiado');
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" /> Copiar enlace
                </Button>
              </>
            ) : (
              <Button
                disabled={enviando}
                onClick={async () => {
                  if (!firmaDlg) return;
                  setEnviando(true);
                  try {
                    const res = await generarLinkFirma(firmaDlg.cert, { email: firmaDlg.email || null });
                    setFirmaDlg({ ...firmaDlg, url: res.url });
                    toast.success('Enlace de firma generado');
                  } catch (e: any) {
                    toast.error(e?.message || 'No se pudo generar el enlace');
                  } finally {
                    setEnviando(false);
                  }
                }}
              >
                <Send className="h-4 w-4 mr-2" /> {enviando ? 'Generando…' : 'Generar enlace'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Subir certificado firmado */}
      <Dialog open={!!subirFirmadoDlg} onOpenChange={(o) => !o && setSubirFirmadoDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subir certificado firmado</DialogTitle>
            <DialogDescription>
              Sube el PDF firmado por el cliente. Reemplazará el PDF actual y el certificado pasará a "emitido".
            </DialogDescription>
          </DialogHeader>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file || !subirFirmadoDlg) return;
              setSubiendoFirmado(true);
              try {
                await subirCertificadoFirmado(subirFirmadoDlg, file);
                toast.success('Certificado firmado guardado');
                setSubirFirmadoDlg(null);
              } catch (err: any) {
                toast.error(err?.message || 'Error al subir el archivo');
              } finally {
                setSubiendoFirmado(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubirFirmadoDlg(null)}>Cancelar</Button>
            <Button
              disabled={subiendoFirmado}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" /> {subiendoFirmado ? 'Subiendo…' : 'Seleccionar archivo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Anular certificado */}
      <Dialog open={!!anularDlg} onOpenChange={(o) => !o && setAnularDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Anular certificado</DialogTitle>
            <DialogDescription>
              Marca el certificado {anularDlg?.numero_formateado} como anulado. Podrás reactivarlo más tarde.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAnularDlg(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!anularDlg) return;
                try {
                  await actualizarCertificado(anularDlg.id, {
                    estado: 'anulado',
                    fecha_anulacion: new Date().toISOString(),
                  } as any);
                  toast.success('Certificado anulado');
                  setAnularDlg(null);
                } catch (e: any) {
                  toast.error(e.message);
                }
              }}
            >
              <Ban className="h-4 w-4 mr-2" /> Confirmar anulación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
